'use client';
import React from 'react';
import { motion } from 'framer-motion';
import BlueHeaderSection from '../BlueHeader';

import aiImg1 from "../../asset/images/gen-ai1.png";
import aiImg2 from "../../asset/images/gen-ai2.png";
import aiImg3 from "../../asset/images/gen-ai3.png";
import aiImg4 from "../../asset/images/gen-ai4.png";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const aiServices = [
  {
    id: "genai-srv-1",
    title: "AI-Powered Content Generation",
    description: "Create high-quality AI-generated content, blogs, social media copy, and product descriptions at scale.",
  },
  {
    id: "genai-srv-2",
    title: "Text-to-Image & Image-to-Image AI",
    description: "Generate compelling visuals from text prompts or transform existing creatives using state-of-the-art AI models.",
  },
  {
    id: "genai-srv-3",
    title: "Chatbots & Virtual Assistants",
    description: "Deploy intelligent agents for customer support, onboarding, lead generation, and internal workflows.",
  },
  {
    id: "genai-srv-4",
    title: "Code Generation & Automation",
    description: "Accelerate development with AI-assisted coding, debugging, and custom script generation.",
  },
  {
    id: "genai-srv-5",
    title: "Voice & Speech AI",
    description: "Design natural voice interfaces and audio agents to power voice-first experiences.",
  },
  {
    id: "genai-srv-6",
    title: "Data-to-Insights Generation",
    description: "Turn raw data into summaries, reports, and visual insights using AI-driven analytics.",
  }
];

const aiFeatures = [
  {
    id: "genai-feat-1",
    title: "Custom AI Models",
    description: "We design and fine-tune models around your domain, data, and workflows instead of forcing one-size-fits-all solutions.",
    image: aiImg1
  },
  {
    id: "genai-feat-2",
    title: "Scalable Solutions",
    description: "From MVPs to large-scale deployments, our architectures are built to scale securely across cloud and hybrid environments.",
    image: aiImg2
  },
  {
    id: "genai-feat-3",
    title: "Cross-Industry Expertise",
    description: "We bring experience across SaaS, fintech, ecommerce, and enterprise to design AI that fits real business use cases.",
    image: aiImg3
  },
  {
    id: "genai-feat-4",
    title: "Ethical AI Practices",
    description: "We prioritize data privacy, transparency, and responsible AI use to align with your compliance and governance needs.",
    image: aiImg4
  }
];

const GenAi = () => {
  return (
    <div className="overflow-hidden">
      <BlueHeaderSection heading="GENERATIVE AI" url="Generative AI" showServices={true} />

      {/* Content Section */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">

          {/* Main Heading */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Transforming Ideas into Intelligent Reality
            </h2>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto"></div>
          </div>

          {/* Intro */}
          <div className="text-center mb-16">
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At **BytesFarms Technologies**, we harness the power of generative AI to unlock new levels of automation, creativity, and personalization.
            </p>
          </div>

          {/* Services List */}
          <div className="grid md:grid-cols-2 gap-8">
            {aiServices.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="p-6 rounded-2xl bg-white shadow-md border-l-4 border-[#f7941d] hover:shadow-lg transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-[#f7941d] mb-3">{item.title}</h4>
                <p className="text-gray-800 leading-relaxed tracking-tight">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section - Fixed Rendering */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why choose BytesFarms for Generative AI?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {aiFeatures.map((feature) => (
              <motion.div
                key={feature.id} // Unique ID prevents double rendering
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[220px] md:h-[250px] bg-gray-900"
              >
                <img
                  src={feature.image}
                  alt="" // Decorative image; text is provided in H3 below
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/75 transition-opacity duration-300"></div>
                
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-center text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GenAi;